import type { OnboardingMember } from './api/coachMatchingApi';

export interface FormattedMemberAnswer {
  label: string;
  value: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function toAnswerLabel(answer: Record<string, unknown>, index: number): string {
  const field = answer.field;
  if (isRecord(field)) {
    const title = field.title;
    if (typeof title === 'string' && title.trim() !== '') return title;
    const ref = field.ref;
    if (typeof ref === 'string' && ref.trim() !== '') return ref;
    const id = field.id;
    if (typeof id === 'string' && id.trim() !== '') return id;
  }
  return `Question ${index + 1}`;
}

function stringifyValue(value: unknown): string {
  if (value == null) return 'Not provided';
  if (typeof value === 'string') return value.trim() === '' ? 'Not provided' : value;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';

  if (Array.isArray(value)) {
    if (value.length === 0) return 'Not provided';
    return value.map((item) => stringifyValue(item)).join(', ');
  }

  if (isRecord(value)) {
    const label = value.label;
    if (typeof label === 'string' && label.trim() !== '') return label;

    const labels = value.labels;
    if (Array.isArray(labels) && labels.length > 0) {
      return labels.map((item) => stringifyValue(item)).join(', ');
    }
  }

  return JSON.stringify(value, null, 2);
}

function toAnswerValue(answer: Record<string, unknown>): string {
  const priorityKeys = [
    'text',
    'email',
    'choice',
    'choices',
    'boolean',
    'number',
    'date',
    'phone_number',
    'url',
    'file_url',
  ];

  for (const key of priorityKeys) {
    if (key in answer) {
      return stringifyValue(answer[key]);
    }
  }

  const fallbackEntry = Object.entries(answer).find(([key]) => key !== 'type' && key !== 'field');
  if (!fallbackEntry) return 'Not provided';
  return stringifyValue(fallbackEntry[1]);
}

export function formatMemberAnswers(answers: unknown): FormattedMemberAnswer[] {
  if (!Array.isArray(answers)) {
    return [{ label: 'Onboarding payload', value: stringifyValue(answers) }];
  }

  return answers.map((answer, index) => {
    if (!isRecord(answer)) {
      return {
        label: `Question ${index + 1}`,
        value: stringifyValue(answer),
      };
    }

    return {
      label: toAnswerLabel(answer, index),
      value: toAnswerValue(answer),
    };
  });
}

export function memberSearchText(member: OnboardingMember): string {
  return [
    member.first_name,
    member.last_name,
    member.email,
    member.company,
    member.form_response_id,
  ]
    .filter((value): value is string => typeof value === 'string' && value.trim() !== '')
    .join(' ')
    .toLowerCase();
}

export function memberDisplayLabel(member: OnboardingMember): string {
  const name = [member.first_name ?? '', member.last_name ?? ''].join(' ').trim();
  if (name && member.company) return `${name} (${member.company})`;
  if (name) return name;
  if (member.email) return member.email;
  return member.form_response_id;
}
